import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { viewSharedResume } from '../services/shareService';
import { FeedbackForm } from '../components/sharing';

const SharedResumePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    const fetchResume = async () => {
      try {
        const response = await viewSharedResume(token);
        if (response.success) {
          setData(response.data);
        } else {
          setError(response.error || 'Failed to load resume');
        }
      } catch (err) {
        console.error('Error loading shared resume:', err);
        if (err.response?.status === 404) {
          setError('This share link is invalid or has expired.');
        } else if (err.response?.status === 410) {
          setError('This share link has expired.');
        } else {
          setError('Failed to load resume. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchResume();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading resume...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Unable to Load Resume
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Build Your Own Resume
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-xl font-bold text-primary-600">
              Kwikly
            </a>
            <span className="text-sm text-gray-500">Resume Preview</span>
          </div>

          {data?.canComment && !feedbackSubmitted && (
            <button
              onClick={() => setShowFeedback(!showFeedback)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
            >
              {showFeedback ? 'Hide Feedback' : 'Give Feedback'}
            </button>
          )}
        </div>

        {/* Owner message */}
        {data?.message && (
          <div className="bg-primary-50 border-t border-primary-100">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <p className="text-sm text-primary-800">
                <span className="font-medium">Message:</span> {data.message}
              </p>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className={`grid gap-8 ${showFeedback ? 'lg:grid-cols-3' : ''}`}>
          {/* Resume preview */}
          <div className={showFeedback ? 'lg:col-span-2' : ''}>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {data?.title && (
                <div className="px-6 py-4 border-b border-gray-100">
                  <h1 className="text-lg font-semibold text-gray-900">
                    {data.title}
                  </h1>
                </div>
              )}
              <div className="p-4">
                <div
                  className="bg-white mx-auto"
                  style={{
                    width: '100%',
                    maxWidth: '816px'
                  }}
                >
                  <iframe
                    srcDoc={data?.html}
                    title="Resume Preview"
                    className="w-full border-0"
                    style={{ height: '1056px' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Feedback panel */}
          {showFeedback && (
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <FeedbackForm
                  shareToken={token}
                  onSubmitted={() => {
                    setFeedbackSubmitted(true);
                    setShowFeedback(false);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA Footer */}
      <footer className="bg-white border-t border-gray-200 py-12 mt-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Build Your Professional Resume
          </h2>
          <p className="text-gray-600 mb-6">
            Create an ATS-optimized resume in minutes with our AI-powered builder.
            No design skills needed.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Get Started Free
          </a>
        </div>
      </footer>
    </div>
  );
};

export default SharedResumePage;
